/* eslint-disable @typescript-eslint/no-unused-vars */
import { createContext, ReactNode, useCallback, useReducer, useState } from 'react';
import { AuthUser, AuthState, ActionMap, ContextType, UserRole, UserSubRole } from '../types/auth';
import { AUTH_INIT_COMPANY_SESS, AUTH_INIT_COMPANY_TOKEN } from '../util/constants';

const INITIALIZE = 'INITIALIZE';
const SIGN_IN = 'SIGN_IN';
const SIGN_OUT = 'SIGN_OUT';

type AuthActionTypes = {
  [INITIALIZE]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [SIGN_IN]: {
    user: AuthUser;
    accessToken: string;
  };
  [SIGN_OUT]: null;
};

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  userRole: null,
  userSubRole: null,
};

const AuthReducer = (state: AuthState, action: ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>]) => {
  switch (action.type) {
    case INITIALIZE:
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        isLoading: true,
        user: action.payload.user,
      };
    case SIGN_IN:
      return {
        ...state,
        isAuthenticated: true,
        accessToken: action.payload.accessToken,
        isLoading: false,
        user: action.payload.user,
      };
    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        user: null,
      };

    default:
      return state;
  }
};

const AuthContext = createContext<ContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(AuthReducer, initialState);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userSubRole, setUserSubRole] = useState<UserSubRole | null>(null);

  const signIn = useCallback(
    async (user: AuthUser, navigateTo?: string) => {
      dispatch({
        type: INITIALIZE, // Using INITIALIZE ensures isInitialized is set true
        payload: {
          isAuthenticated: true,
          user: user,
        },
      });

      const currentCompanyRole = user?.role;

      if (currentCompanyRole) {
        setUserRole(currentCompanyRole!.role);
      }

      if (currentCompanyRole?.subRole) {
        setUserSubRole(currentCompanyRole!.subRole);
      }
    },
    [dispatch], // Added dispatch to dependency array
  );

  const signOut = async () => {
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem(AUTH_INIT_COMPANY_SESS);
    localStorage.removeItem(AUTH_INIT_COMPANY_TOKEN);
    dispatch({
      type: SIGN_OUT,
      payload: null,
    });
    setUserRole(null);
    setUserSubRole(null);
  };

  const initializeUser = async () => {
    dispatch({
      type: INITIALIZE,
      payload: {
        isAuthenticated: false,
        user: null,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        signIn,
        signOut,
        userRole,
        userSubRole,
        initializeUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
